'use client';
import React, { useContext, useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { FaSpinner } from 'react-icons/fa';

import { SessionContext } from '@benzinga/session-context';
import { PlusProduct } from '@benzinga/plus-manager';
import { SubscriptionsManager, UserSubscription } from '@benzinga/subscription-manager';
import { Card, IPrecheckoutResponse, ShopManager } from '@benzinga/shop-manager';

import { LeftArrow } from '@benzinga/themed-icons';
import { DowngradeIcon, MonthlyIcon, RedeemIcon } from './Icons';
import { OrderSummary, CancelSurvey, CreditCardBlock, AlternativeCard, isProToPro } from './components';
import { TrackingManager } from '@benzinga/tracking-manager';

interface CancelFlowProps {
  creditCards: Card[];
  setShowCancel: React.Dispatch<React.SetStateAction<boolean>>;
  setShowInitial: React.Dispatch<React.SetStateAction<boolean>>;
  handleClose: () => void;
  subscription: UserSubscription;
  matchingProduct: PlusProduct | null;
  monthlyPlan: PlusProduct | null;
  handleSubscriptionCheckout: (IPrecheckoutResponse, coupon, oldSub, paymentCard) => Promise<boolean>;
  handleSubscriptionUpgrade: (IPrecheckoutResponse, coupon, paymentCard) => Promise<boolean>;
}

const discount = {
  month: {
    coupon: 'cancel-save-monthly',
    description: '50% off your next two months',
    discount: '50% off for 2 months',
    heading: 'Two Months',
  },
  year: {
    coupon: 'cancel-save-annual-2',
    description: '25% off on your next renewal',
    discount: '25% off',
    heading: 'Next Renewal',
  },
};

export const getEndPeriodDate = subscription => {
  if (subscription.currentPeriodEnd) {
    return dayjs(subscription.currentPeriodEnd).format('MMM DD YYYY');
  } else {
    return dayjs().format('MMM DD YYYY');
  }
};

export const getNewEndPeriodDate = (subscription, subInterval) => {
  // only 2 save coupons available: 2 months for monthly and 1 year for annual
  const discountPeriod = subInterval === 'month' ? 2 : 1;
  if (subscription.currentPeriodEnd) {
    return dayjs(subscription.currentPeriodEnd).add(discountPeriod, subInterval).format('MMM DD YYYY');
  } else {
    return dayjs().add(discountPeriod, subInterval).format('MMM DD YYYY');
  }
};

export const CancelFlow: React.FC<CancelFlowProps> = ({
  creditCards,
  handleClose,
  handleSubscriptionCheckout,
  handleSubscriptionUpgrade,
  matchingProduct,
  monthlyPlan,
  setShowCancel,
  setShowInitial,
  subscription,
}) => {
  const [showSurvey, setShowSurvey] = useState(true);
  const [showAlternatives, setShowAlternatives] = useState(false);
  const [showPlanChangeReview, setShowPlanChangeReview] = useState(false);
  const [showCancelled, setShowCancelled] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [showError, setShowError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [cancelReason, setCancelReason] = useState<string>('');

  const [selectedPlan, setSelectedPlan] = useState<IPrecheckoutResponse | null>(null);
  const [selectedInterval, setSelectedInterval] = useState('');
  const [selectedCard, setSelectedCard] = useState(creditCards[0] ?? null);

  const [redeemGift, setRedeemGift] = useState(false);
  const [switchToMonthly, setSwitchToMonthly] = useState(false);
  const [matchingCheckouts, setMatchingCheckouts] = useState<IPrecheckoutResponse[]>([]);
  const [discountCheckout, setDiscountCheckout] = useState<IPrecheckoutResponse | null>(null);
  const [monthlyCheckout, setMonthlyCheckout] = useState<IPrecheckoutResponse | null>(null);

  const [checkoutDetails, setCheckoutDetails] = useState<IPrecheckoutResponse | null>(null);
  const [summaryDetails, setSummaryDetails] = useState<{ body: string[]; description: string; title: string } | null>(
    null,
  );
  const [reviewHeader, setReviewHeader] = useState<React.ReactElement | null>(null);

  const session = useContext(SessionContext);
  const subscriptionManager = session.getManager(SubscriptionsManager);

  const handleCancelSubmit = async () => {
    setIsLoading(true);
    try {
      const res = await subscriptionManager.deleteSubscription(subscription.uuid);
      if (res.err) {
        const errMessage = await (res.err?.data as Response)?.json();
        throw errMessage;
      } else {
        await session.getManager(TrackingManager).trackSubscriptionEvent('cancelled', {
          interval: subscription.interval,
          package_id: subscription.uuid,
          price: subscription.amount,
          product_id: subscription.product,
          reason: cancelReason ? cancelReason : undefined,
        });
        setShowCancelled(true);
        setShowAlternatives(false);
        goToSummary('');
      }
    } catch (err) {
      console.log('Error cancelling subscription', err);
    }
    setIsLoading(false);
  };

  const handleConfirmChange = async () => {
    setShowError(false);
    setIsLoading(true);
    let type = '';

    if (redeemGift) {
      type = 'discount';
    } else if (switchToMonthly) {
      type = 'switchToMonthly';
    } else if (isProToPro(subscription, checkoutDetails)) {
      type = 'proUpgrade';
    } else {
      type = 'planChange';
    }

    try {
      let result = false;
      if (type === 'planChange') {
        result = await handleSubscriptionCheckout(
          checkoutDetails,
          discount[selectedInterval]?.coupon,
          subscription,
          selectedCard,
        );
      } else {
        result = await handleSubscriptionUpgrade(checkoutDetails, discount[selectedInterval]?.coupon, selectedCard);
      }
      if (result) {
        setShowPlanChangeReview(false);
        goToSummary(type);
      }
      setShowError(!result);
      setIsLoading(false);
    } catch (err) {
      console.log('Error completing order request', err);
      setShowError(true);
      setIsLoading(false);
    }
  };

  const goToAlternatives = () => {
    setShowSurvey(false);
    setShowAlternatives(true);
  };

  const goToSummary = type => {
    const summary = getSummary(type);
    setSummaryDetails(summary);
    setShowSummary(true);
  };

  const handleGoBack = () => {
    if (showSurvey) {
      setShowCancel(false);
      setShowInitial(true);
    } else if (showAlternatives) {
      setShowAlternatives(false);
      setShowSurvey(true);
    } else if (showPlanChangeReview) {
      setShowPlanChangeReview(false);
      setShowAlternatives(true);
      setSelectedInterval('');
      setRedeemGift(false);
      setSwitchToMonthly(false);
      setShowError(false);
    }
  };

  const getSummary = type => {
    switch (type) {
      case 'discount':
        return {
          body: [
            `The discount will be applied starting on the next billing date of ${getEndPeriodDate(subscription)}. Your
            regular subscription will resume on ${getNewEndPeriodDate(subscription, subscription.interval)}.`,
          ],
          description: `We'd hate to see you go, this is why Benzinga is offering you ${discount[selectedInterval]?.description}`,
          title: `${discount[selectedInterval]?.heading} Discount`,
        };
      case 'switchToMonthly':
        return {
          body: [
            `Your annual subscription will change to monthly on ${getEndPeriodDate(subscription)}.`,
            `The discount is applied for the first 2 months. Your regular billing price will start on
            ${getNewEndPeriodDate(subscription, 'month')} and will be billed at
            ${checkoutDetails?.total_price}/${checkoutDetails?.interval}.`,
          ],
          description: 'Switch to a monthly plan with no commitment and get 2 months for 50% off',
          title: 'Change to Monthly',
        };
      case 'planChange':
        return {
          body: [
            `Your subscription will be cancelled on ${getEndPeriodDate(subscription)}. You will have access to ${
              subscription.planName
            } features until then.`,
            `Your new subscription will start on ${dayjs().format('MMM DD YYYY')} and will be billed
            ${checkoutDetails?.new_final_price}/${checkoutDetails?.interval}.`,
          ],
          description: 'Try a different tier with a bonus discount',
          title: `Change to ${selectedPlan?.plan.name}`,
        };
      default:
        return {
          body: [
            `You will no longer have access to ${subscription.planName} features after ${
              subscription.currentPeriodEnd ? dayjs(subscription.currentPeriodEnd).format('MMM DD YYYY') : 'today'
            }.`,
          ],
          description: "We're sad to see you go.",
          title: 'Cancelled Subscription',
        };
    }
  };

  const handleShowPlanReview = async ({ interval = '', plan, type = '' }) => {
    let title = `Change Plan - Change to ${plan?.plan?.name}`;
    let description = 'Try a different tier with a bonus discount';

    if (type === 'gift') {
      setRedeemGift(true);
      title = discount[interval]?.heading + ' Discount';
      description = `We'd hate to see you go, this is why Benzinga is offering you ${discount[interval]?.description}`;
    }
    if (type === 'goMonthly') {
      setSwitchToMonthly(true);
      title = 'Switch to Monthly';
      description = 'Switch to a monthly plan with no commitment and get 2 months for 50% off';
    }

    setSelectedInterval(interval);
    setCheckoutDetails(plan);

    const header = (
      <>
        <div className="font-bold text-lg">{title}</div>
        <p className="text-sm my-2"> {description}</p>
      </>
    );

    setReviewHeader(header);
    setSelectedPlan(plan);
    setShowAlternatives(false);
    setShowPlanChangeReview(true);
  };

  useEffect(() => {
    const getPrecheckouts = async () => {
      const precheckouts = matchingProduct?.purchaseSlugs?.map(async plan => {
        let interval = 'month';
        if (plan.includes('annual') || plan.includes('year')) {
          interval = 'year';
        }
        const checkout = await session.getManager(ShopManager).preCheckout({
          coupon: discount[interval].coupon,
          selected_plan: plan,
          subscription: '',
        });
        return checkout.ok;
      });
      let precheckoutsResolved = await Promise.all(precheckouts ?? []);
      precheckoutsResolved = precheckoutsResolved.filter(checkout => checkout !== undefined);
      setMatchingCheckouts(precheckoutsResolved as IPrecheckoutResponse[]);
    };

    const getDiscountCheckout = async () => {
      const checkout = await session.getManager(ShopManager).preCheckout({
        coupon: discount[subscription?.interval]?.coupon,
        selected_plan: subscription?.basePlan,
        subscription: '',
      });
      if (checkout.ok) {
        setDiscountCheckout(checkout.ok);
      }
    };

    const getMonthlyCheckout = async () => {
      const checkout = await session.getManager(ShopManager).preCheckout({
        coupon: discount['month']?.coupon,
        selected_plan: monthlyPlan?.slug,
        subscription: '',
      });
      if (checkout.ok) {
        setMonthlyCheckout(checkout.ok);
      }
    };

    getDiscountCheckout();
    if (matchingProduct?.purchaseSlugs && matchingProduct.purchaseSlugs.length > 0) {
      getPrecheckouts();
    }
    if (monthlyPlan) {
      getMonthlyCheckout();
    }
  }, [matchingProduct, monthlyPlan, monthlyPlan?.slug, session, subscription?.basePlan, subscription?.interval]);

  return (
    <div className="h-full w-full relative">
      {showSurvey && (
        <CancelSurvey goToAlternatives={goToAlternatives} handleClose={handleClose} setCancelReason={setCancelReason} />
      )}
      {showAlternatives && (
        <div>
          <div className="font-bold text-lg">Cancel Subscription </div>
          <p className="text-sm my-2">We&apos;re sorry to see you go. Are you sure?</p>
          <div className="flex flex-col gap-2 max-h-[440px] overflow-y-auto">
            {subscription.interval !== 'onetime' && discountCheckout && (
              <AlternativeCard
                body={`We'd hate to see you go, this is why Benzinga is offering you ${
                  discount[subscription.interval]?.description
                }`}
                handleClick={() => {
                  handleShowPlanReview({ interval: subscription.interval, plan: discountCheckout, type: 'gift' });
                }}
                icon={<RedeemIcon />}
                label="Redeem Gift"
                rbgColor="21, 128, 61"
                title={`${discount[subscription.interval]?.heading} Discount`}
              />
            )}
            {/* Switch to Monthly only for annual products that have a monthly plan */}
            {monthlyPlan && monthlyCheckout && (
              <AlternativeCard
                body="Switch to monthly plan with no commitment and get 2 months for 50% off"
                handleClick={() => {
                  handleShowPlanReview({ interval: 'month', plan: monthlyCheckout, type: 'goMonthly' });
                }}
                icon={<MonthlyIcon />}
                label="Switch to Monthly"
                rbgColor="51, 65, 85"
                title="Change to Monthly"
              />
            )}
            {matchingCheckouts.length > 0 &&
              matchingCheckouts.map(plan => {
                return (
                  <AlternativeCard
                    body={`Try to a different tier with a bonus discount`}
                    handleClick={() => {
                      handleShowPlanReview({ interval: plan?.interval, plan });
                    }}
                    icon={<DowngradeIcon />}
                    key={plan?.plan?.name}
                    label="Change Plan"
                    rbgColor="55, 48, 163"
                    title={`Change Plan - ${plan?.plan?.name}`}
                  />
                );
              })}
          </div>
          <div className="text-sm flex gap-2 absolute bottom-0 right-0">
            <button className="border border-bzgrey-400 px-2 py-1 text-bzgrey-400 rounded-md" onClick={handleClose}>
              Close
            </button>
            <button
              className="border border-red-500 bg-red-500 px-2 py-1 text-white rounded-md"
              disabled={isLoading}
              onClick={handleCancelSubmit}
            >
              {isLoading ? (
                <div className="flex flex-row items-center cursor-wait">
                  <FaSpinner className="animate-spin mr-2 text-white" />
                  <span>Working on your request</span>
                </div>
              ) : (
                'Cancel Subscription'
              )}
            </button>
          </div>
        </div>
      )}
      {showPlanChangeReview && (
        <div className="flex flex-col justify-between h-full overflow-y-auto">
          <div>
            {reviewHeader && reviewHeader}
            {selectedInterval && checkoutDetails && (
              <>
                <OrderSummary
                  checkout={checkoutDetails}
                  discount={discount[selectedInterval].discount}
                  title="New Plan Summary"
                />
                <CreditCardBlock
                  creditCards={creditCards}
                  selectedCard={selectedCard}
                  setSelectedCard={setSelectedCard}
                />
              </>
            )}
          </div>
          {showError && (
            <p className="text-red-500">
              There was an issue completing your order. Please try again later or contact our support team.
            </p>
          )}
          <button
            className={`px-4 py-2 mx-auto mt-4 rounded-md w-full ${
              selectedInterval && selectedPlan && creditCards.length > 0 && checkoutDetails
                ? 'bg-bzblue-700 text-white'
                : 'bg-bzgrey-300/80 text-bzgrey-400'
            }`}
            disabled={!checkoutDetails || !selectedCard || isLoading}
            onClick={handleConfirmChange}
          >
            {isLoading ? (
              <div className="w-full flex justify-center">
                <FaSpinner className="animate-spin mr-2 h-4 w-4 text-white my-1" />
              </div>
            ) : (
              'Complete Plan Changes'
            )}
          </button>
        </div>
      )}
      {showSummary && summaryDetails && (
        <div>
          <div className="font-bold text-lg">{summaryDetails.title}</div>
          <p className="text-sm mb-2">{summaryDetails.description}</p>
          <div className="py-4">
            <h2>{showCancelled ? 'Your request has been completed.' : "We're happy to see you stay!"}</h2>
            {!showCancelled && (
              <OrderSummary
                checkout={checkoutDetails}
                discount={discount[selectedInterval]?.discount}
                subscriptionToPause={null}
                title={'Order Completed'}
              />
            )}
            <div className="pt-2">
              {summaryDetails.body.map((b, idx) => (
                <p key={idx}>{b}</p>
              ))}
            </div>
            <div className="text-sm flex gap-2 absolute bottom-0 right-0">
              <button className="border border-bzgrey-400 px-2 py-1 text-bzgrey-400 rounded-md" onClick={handleClose}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Back button */}
      {(showSurvey || showAlternatives || showPlanChangeReview) && (
        <button className="absolute -top-4 -left-4" onClick={handleGoBack}>
          <LeftArrow />
        </button>
      )}
    </div>
  );
};
